import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../App';
import { useTheme } from '../contexts/ThemeContext';
import api from '../lib/api';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription,
  DialogFooter 
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { cn } from '../lib/utils';
import { 
  Users, Plus, Trophy, Flame, Target, Calendar as CalendarIcon, 
  Crown, Copy, ExternalLink, ArrowRight, Sparkles, Share2, Link2
} from 'lucide-react';

export default function SharedGoals() {
  const { user } = useAuth();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [sharedGoals, setSharedGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [filter, setFilter] = useState('all'); // all, active, completed
  
  // Join with code state
  const [joinCode, setJoinCode] = useState('');
  const [joiningWithCode, setJoiningWithCode] = useState(false);

  // Create goal form state
  const [newGoal, setNewGoal] = useState({
    title: '',
    description: '',
    target_value: 10,
    target_unit: 'times',
    deadline: null,
    max_participants: 10,
  });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    // Any logged-in user can view their joined shared goals
    fetchSharedGoals();
  }, [user]);

  const fetchSharedGoals = async () => {
    try {
      const res = await api.get('/api/shared-goals');
      setSharedGoals(res.data);
    } catch (err) {
      toast.error('Failed to load shared goals');
    } finally {
      setLoading(false);
    }
  };

  const handleJoinWithCode = async () => {
    if (!joinCode.trim()) {
      toast.error('Please enter an invite code');
      return;
    }
    
    setJoiningWithCode(true);
    try {
      // Navigate to the join page with the code
      navigate(`/join/${joinCode.trim().toUpperCase()}`);
    } catch (err) {
      toast.error('Failed to join with code');
    } finally {
      setJoiningWithCode(false);
      setShowJoinModal(false);
    }
  };

  const handleCreateGoal = async () => {
    if (!newGoal.title.trim()) {
      toast.error('Please enter a goal title');
      return;
    }
    if (!newGoal.deadline) {
      toast.error('Please select a deadline');
      return;
    }
    if (newGoal.target_value < 1) {
      toast.error('Target must be at least 1');
      return;
    }

    setCreating(true);
    try {
      const res = await api.post('/api/shared-goals', {
        ...newGoal,
        deadline: newGoal.deadline.toISOString()
      });
      
      toast.success('Shared goal created!');
      setShowCreateModal(false);
      setSelectedGoal(res.data);
      setShowInviteModal(true);
      
      // Reset form
      setNewGoal({
        title: '',
        description: '',
        target_value: 10,
        target_unit: 'times',
        deadline: null,
        max_participants: 10,
      });
      
      fetchSharedGoals();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create goal');
    } finally {
      setCreating(false);
    }
  };

  const copyInviteLink = (goal) => {
    const link = `${window.location.origin}/join/${goal.invite_code}`;
    navigator.clipboard.writeText(link);
    toast.success('Invite link copied!');
  };

  const isDone = (goal) =>
    ['COMPLETED', 'EXPIRED'].includes(goal.status) ||
    goal.user_status === 'COMPLETED' ||
    goal.user_progress >= goal.target_value;

  const filteredGoals = sharedGoals.filter(goal => {
    if (filter === 'active') return goal.status === 'ACTIVE' && !isDone(goal);
    if (filter === 'completed') return isDone(goal);
    return true;
  });

  const handleLogProgress = async (goalId, e) => {
    if (e) e.stopPropagation();
    try {
      const res = await api.put(`/api/shared-goals/${goalId}/progress`, {
        progress_delta: 1
      });
      
      toast.success(`+1 logged! (${res.data.current_progress}/${res.data.target_value})`);
      
      // Update local state immediately
      setSharedGoals(prev => prev.map(g => 
        g.id === goalId 
          ? { ...g, user_progress: res.data.current_progress, user_status: res.data.status }
          : g
      ));
      
      if (res.data.status === 'COMPLETED') {
        toast.success('Shared goal completed! 🎉');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to log progress');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'ACTIVE': return 'bg-green-500/10 text-green-600 border-green-500/20';
      case 'COMPLETED': return 'bg-blue-500/10 text-blue-600 border-blue-500/20';
      case 'EXPIRED': return 'bg-coral/10 text-coral border-coral/20';
      case 'CANCELLED': return 'bg-red-500/10 text-red-600 border-red-500/20';
      default: return 'bg-white0/10 text-[#5F5F5F] border-gray-500/20';
    }
  };

  return (
    <div className={cn(
      "min-h-screen transition-colors duration-300",
      isDark ? "bg-[#050505]" : "bg-[#FAF9F5]"
    )}>
      <Navbar />
      
      <main className="container mx-auto px-4 py-10 max-w-6xl">
        {/* Clean header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-10">
          <div>
            <p className="sl-section-tag mb-3">SHARED CHALLENGES</p>
            <h1 className="text-3xl sm:text-4xl font-semibold tracking-[-0.03em] text-[#1A1A1A] leading-tight">
              Together.
            </h1>
            <p className="mt-3 text-sm sm:text-base text-[#5F5F5F] max-w-md">
              Pick a goal with friends. Show up for each other.
            </p>
          </div>

          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setShowJoinModal(true)}
              data-testid="join-with-code-btn"
              className="sl-pill-ghost h-auto"
            >
              <Link2 className="w-4 h-4" />
              Join with Code
            </Button>

            {user?.is_premium ? (
              <Button
                onClick={() => setShowCreateModal(true)}
                className="sl-pill-dark h-auto"
                data-testid="create-shared-goal-btn"
              >
                <Plus className="w-4 h-4" />
                Create
              </Button>
            ) : (
              <Button 
                onClick={() => navigate('/upgrade')}
                variant="outline"
                className="border-coral/50 text-coral hover:bg-[#FDF0EC]0/10"
                data-testid="upgrade-to-create-btn"
              >
                <Crown className="w-4 h-4 mr-2" />
                Upgrade to Create
              </Button>
            )}
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-2 mb-6">
          {['all', 'active', 'completed'].map(tab => (
            <Button
              key={tab}
              variant="ghost"
              size="sm"
              onClick={() => setFilter(tab)}
              className={cn(
                "capitalize rounded-full px-4 transition-colors duration-200",
                filter === tab ? "bg-[#1A1A1A] text-white hover:bg-[#1A1A1A] hover:text-white" : "bg-white border border-[#EEEAE0] text-[#5F5F5F] hover:bg-[#F4F3EE]"
              )}
              data-testid={`filter-${tab}`}
            >
              {tab}
            </Button>
          ))}
        </div>

        {/* Goals Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className={cn("animate-spin rounded-full h-8 w-8 border-b-2", isDark ? "border-violet-400" : "border-violet-500")}></div>
          </div>
        ) : filteredGoals.length === 0 && sharedGoals.length > 0 ? (
          <div className="rounded-2xl border border-dashed border-[#EEEAE0] bg-white py-14 text-center" data-testid="shared-goals-filter-empty">
            <p className="text-base font-semibold text-[#1A1A1A]">No {filter} shared goals</p>
            <p className="mt-2 text-sm text-[#9E8778]">
              {filter === 'completed' ? 'Finish a shared challenge and it will show up here.' : 'All your shared challenges are finished.'}
            </p>
            <Button variant="ghost" onClick={() => setFilter('all')} className="sl-pill-ghost h-auto mt-5" data-testid="shared-goals-show-all-btn">
              Show all
            </Button>
          </div>
        ) : filteredGoals.length === 0 ? (
          <Card className={cn(
            "border-dashed border-2",
            isDark ? "glass-card border-white/10" : "bg-white border-[#EEEAE0] shadow-sm"
          )}>
            <CardContent className="flex flex-col items-center justify-center py-16">
              <Users className={cn("w-16 h-16 mb-4", isDark ? "text-zinc-600" : "text-[#9E8778]")} />
              <h3 className={cn("text-xl font-semibold mb-2", isDark ? "text-white" : "text-[#1A1A1A]")}>
                {filter === 'all' ? 'No shared goals yet' : `No ${filter} goals`}
              </h3>
              <p className={cn("text-center max-w-md mb-6", isDark ? "text-zinc-400" : "text-[#9E8778]")}>
                {user?.is_premium 
                  ? "Create a shared goal and invite friends to achieve it together. Track each other's progress and stay accountable!"
                  : "Join a shared goal using an invite link from a friend. You'll be able to track progress and compete on the leaderboard!"}
              </p>
              {user?.is_premium ? (
                <Button onClick={() => setShowCreateModal(true)} className="btn-glossy" data-testid="empty-create-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Your First Shared Goal
                </Button>
              ) : (
                <p className={cn("text-sm", isDark ? "text-zinc-500" : "text-[#9E8778]")}>
                  Ask a Pro user to invite you, or <Button variant="link" className="p-0 h-auto text-coral" onClick={() => navigate('/upgrade')}>upgrade to Pro</Button> to create your own goals.
                </p>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {filteredGoals.map(goal => (
              <Card 
                key={goal.id} 
                className={cn(
                  "transition-all cursor-pointer group",
                  isDark 
                    ? "glass-card card-shine hover:border-[#F5D5CB]" 
                    : "bg-white shadow-sm border-[#EEEAE0] hover:shadow-md hover:border-amber-300"
                )}
                onClick={() => navigate(`/shared-goals/${goal.id}`)}
                data-testid={`shared-goal-card-${goal.id}`}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className={cn("text-lg flex items-center gap-2", isDark ? "text-white" : "text-[#1A1A1A]")}>
                        {goal.title}
                        {goal.is_creator && (
                          <Crown className="w-4 h-4 text-coral" title="You created this" />
                        )}
                      </CardTitle>
                      <CardDescription className={cn("mt-1", isDark ? "text-zinc-400" : "text-[#9E8778]")}>
                        {goal.description || `${goal.target_value} ${goal.target_unit}`}
                      </CardDescription>
                    </div>
                    <Badge className={cn("shrink-0 ml-2", getStatusColor(isDone(goal) && goal.status === "ACTIVE" ? "COMPLETED" : goal.status))} data-testid={`shared-goal-status-${goal.id}`}>
                      {isDone(goal) && goal.status === 'ACTIVE' ? 'COMPLETED' : goal.status}
                    </Badge>
                  </div>
                </CardHeader>
                
                <CardContent className="space-y-4">
                  {/* Progress */}
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className={isDark ? "text-zinc-400" : "text-[#9E8778]"}>Your Progress</span>
                      <span className={cn("font-medium", isDark ? "text-white" : "text-[#1A1A1A]")}>
                        {goal.user_progress}/{goal.target_value} {goal.target_unit}
                      </span>
                    </div>
                    <Progress 
                      value={Math.min(100, (goal.user_progress / goal.target_value) * 100)} 
                      className="h-2"
                    />
                  </div>

                  {/* Stats Row */}
                  <div className="flex items-center justify-between text-sm">
                    <div className={cn("flex items-center gap-1", isDark ? "text-zinc-400" : "text-[#9E8778]")}>
                      <Users className="w-4 h-4" />
                      <span>{goal.participant_count} {goal.participant_count === 1 ? 'participant' : 'participants'}</span>
                    </div>
                    
                    {goal.user_streak > 0 && (
                      <div className="flex items-center gap-1 text-coral">
                        <Flame className="w-4 h-4" />
                        <span>{goal.user_streak} day streak</span>
                      </div>
                    )}
                  </div>

                  {/* Deadline */}
                  <div className={cn("flex items-center gap-2 text-sm", isDark ? "text-zinc-400" : "text-[#9E8778]")}>
                    <CalendarIcon className="w-4 h-4" />
                    <span>
                      Ends {format(new Date(goal.deadline), 'MMM d, yyyy')}
                    </span>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-2 pt-2">
                    {goal.status === 'ACTIVE' && goal.user_progress < goal.target_value ? (
                      <button 
                        className="flex-1 inline-flex items-center justify-center gap-2 h-9 px-4 py-2 bg-coral text-white font-medium rounded-full shadow-[0_0_20px_rgba(245,158,11,0.3)] hover:shadow-[0_0_30px_rgba(245,158,11,0.5)] hover:scale-[1.02] active:scale-[0.98] transition-all duration-300"
                        onClick={(e) => handleLogProgress(goal.id, e)}
                        data-testid={`shared-goal-tile-log-${goal.id}`}
                      >
                        <Plus className="w-4 h-4" />
                        Log Progress
                      </button>
                    ) : goal.user_progress >= goal.target_value ? (
                      <Button 
                        variant="outline"
                        className="flex-1 border-emerald-500/50 text-emerald-500 hover:bg-emerald-500/10"
                        disabled
                      >
                        <Trophy className="w-4 h-4 mr-2" />
                        Goal Complete!
                      </Button>
                    ) : null}
                    
                    <Button 
                      variant="ghost" 
                      className={cn(
                        "hover:bg-[#FDF0EC]0/10",
                        isDark ? "text-zinc-300 hover:text-white" : "text-[#5F5F5F] hover:text-[#1A1A1A]"
                      )}
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/shared-goals/${goal.id}`);
                      }}
                      data-testid={`shared-goal-tile-details-${goal.id}`}
                    >
                      Details
                      <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Create Goal Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto overflow-x-visible">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-primary" />
              Create Shared Goal
            </DialogTitle>
            <DialogDescription>
              Set a goal and invite friends to achieve it together
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Title */}
            <div className="space-y-2">
              <Label htmlFor="title">Goal Title *</Label>
              <Input
                id="title"
                placeholder="e.g., Read 10 books together"
                value={newGoal.title}
                onChange={(e) => setNewGoal({ ...newGoal, title: e.target.value })}
                data-testid="goal-title-input"
              />
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                placeholder="What's this goal about?"
                value={newGoal.description}
                onChange={(e) => setNewGoal({ ...newGoal, description: e.target.value })}
                rows={2}
              />
            </div>

            {/* Target */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="target">Target Value *</Label>
                <Input
                  id="target"
                  type="number"
                  min="1"
                  value={newGoal.target_value === 0 ? '' : newGoal.target_value}
                  onChange={(e) => {
                    const val = e.target.value;
                    setNewGoal({ ...newGoal, target_value: val === '' ? 0 : parseInt(val) || 0 });
                  }}
                  onBlur={(e) => {
                    // Reset to 1 if empty or invalid on blur
                    if (!newGoal.target_value || newGoal.target_value < 1) {
                      setNewGoal({ ...newGoal, target_value: 1 });
                    }
                  }}
                  data-testid="goal-target-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="unit">Unit</Label>
                <Input
                  id="unit"
                  placeholder="times, days, hours..."
                  value={newGoal.target_unit}
                  onChange={(e) => setNewGoal({ ...newGoal, target_unit: e.target.value })}
                />
              </div>
            </div>

            {/* Deadline */}
            <div className="space-y-2">
              <Label>Deadline *</Label>
              <Popover modal={true}>
                <PopoverTrigger asChild>
                  <Button 
                    variant="outline" 
                    className="w-full justify-start text-left font-normal"
                    data-testid="deadline-picker"
                    type="button"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {newGoal.deadline ? format(newGoal.deadline, 'PPP') : 'Pick a date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent 
                  className="w-auto p-0 z-[9999]" 
                  align="start"
                  side="bottom"
                  sideOffset={4}
                  avoidCollisions={true}
                  collisionPadding={16}
                >
                  <Calendar
                    mode="single"
                    selected={newGoal.deadline}
                    onSelect={(date) => setNewGoal({ ...newGoal, deadline: date })}
                    disabled={(date) => date < new Date()}
                    initialFocus
                    className="rounded-md border"
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Max Participants */}
            <div className="space-y-2">
              <Label htmlFor="maxParticipants">Max Participants (2-50)</Label>
              <Input
                id="maxParticipants"
                type="number"
                min="2"
                max="50"
                value={newGoal.max_participants}
                onChange={(e) => setNewGoal({ 
                  ...newGoal, 
                  max_participants: Math.min(50, Math.max(2, parseInt(e.target.value) || 2))
                })}
              />
            </div>

            {/* Pledge Toggle */}

          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)} className="glass-button">
              Cancel
            </Button>
            <Button 
              onClick={handleCreateGoal} 
              disabled={creating}
              data-testid="create-goal-submit"
              className="btn-glossy"
            >
              {creating ? 'Creating...' : 'Create & Get Invite Link'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invite Link Modal */}
      <Dialog open={showInviteModal} onOpenChange={setShowInviteModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Share2 className="w-5 h-5 text-primary" />
              Share Your Goal
            </DialogTitle>
            <DialogDescription>
              Invite friends to join &quot;{selectedGoal?.title}&quot;
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Invite Code</Label>
              <div className="flex gap-2">
                <Input 
                  value={selectedGoal?.invite_code || ''} 
                  readOnly 
                  className="font-mono text-lg tracking-wider"
                />
                <Button 
                  variant="outline" 
                  onClick={() => copyInviteLink(selectedGoal)}
                  data-testid="copy-invite-code"
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Invite Link</Label>
              <div className="flex gap-2">
                <Input 
                  value={`${window.location.origin}/join/${selectedGoal?.invite_code || ''}`} 
                  readOnly 
                  className="text-sm"
                />
                <Button 
                  variant="outline" 
                  onClick={() => copyInviteLink(selectedGoal)}
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>

            <p className="text-sm text-muted-foreground">
              Share this link with friends. Anyone can join - no Pro subscription required!
            </p>
          </div>

          <DialogFooter>
            <Button onClick={() => {
              setShowInviteModal(false);
              if (selectedGoal) navigate(`/shared-goals/${selectedGoal.id}`);
            }} className="btn-glossy">
              View Goal
              <ExternalLink className="w-4 h-4 ml-2" />
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Join with Code Modal */}
      <Dialog open={showJoinModal} onOpenChange={setShowJoinModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link2 className="w-5 h-5 text-primary" />
              Join with Invite Code
            </DialogTitle>
            <DialogDescription>
              Enter the invite code shared by your friend
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="inviteCode">Invite Code</Label>
              <Input
                id="inviteCode"
                placeholder="e.g., ABC12345"
                value={joinCode}
                onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                className="font-mono text-lg tracking-wider text-center"
                maxLength={8}
                data-testid="join-code-input"
              />
            </div>
            
            <p className="text-sm text-muted-foreground text-center">
              The invite code is usually 8 characters long
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowJoinModal(false)} className="glass-button">
              Cancel
            </Button>
            <Button 
              onClick={handleJoinWithCode}
              disabled={joiningWithCode || !joinCode.trim()}
              data-testid="join-code-submit"
              className="btn-glossy"
            >
              {joiningWithCode ? 'Joining...' : 'Continue'}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
